import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Table, Tag, Button, Descriptions } from 'antd';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import dayjs from 'dayjs';

const StaffDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const { data: staffData, isLoading: staffLoading } = useQuery(['staff', id], async () => {
        const res = await api.get(`/staff/${id}`);
        return res.data.data;
    });

    const { data: coursesData, isLoading: coursesLoading } = useQuery(['courses', id], async () => {
        const res = await api.get('/courses');
        return res.data.data;
    });

    if (staffLoading || coursesLoading) return <div>{t('loading')}</div>;
    if (!staffData) return <div>{t('staffNotFound')}</div>;

    const staff = staffData;

    // Filter courses where this staff member is the instructor
    const teachingCourses = coursesData?.filter(course =>
        course.instructor?._id === id || course.instructor === id
    ) || [];

    const formatCurrency = (amount, currency = 'USD') => {
        const symbol = currency === 'IQD' ? 'IQD' : '$';
        return `${symbol}${amount?.toLocaleString() || '0'}`;
    };

    const courseColumns = [
        {
            title: t('courseName'),
            dataIndex: 'name',
            key: 'name',
            render: (name, record) => (
                <a
                    onClick={() => navigate(`/courses/${record._id}`)}
                    style={{ cursor: 'pointer', color: '#1890ff' }}
                >
                    {name}
                </a>
            )
        },
        {
            title: t('code'),
            dataIndex: 'code',
            key: 'code'
        },
        {
            title: t('subject'),
            key: 'subject',
            render: (_, record) => {
                if (!record.subject) return '-';
                if (typeof record.subject === 'string') return record.subject;
                if (typeof record.subject.name === 'object') {
                    return record.subject.name[i18n.language] || record.subject.name.en || record.subject.name.ku || record.subject.name.ar || '-';
                }
                return record.subject.name || '-';
            }
        },
        {
            title: t('status'),
            key: 'status',
            render: (_, record) => (
                <Tag color={record.isActive ? 'green' : 'red'}>
                    {record.isActive ? t('active') : t('inactive')}
                </Tag>
            )
        },
    ];

    return (
        <div>
            <Button onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>
                {t('back')}
            </Button>

            <Card title={`${t('staff')}: ${staff.firstName} ${staff.lastName}`}>
                <Descriptions bordered column={2}>
                    <Descriptions.Item label={t('staffId')}>
                        {staff.staffId}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('position')}>
                        {t(staff.position)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('email')}>
                        {staff.email}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('phone')}>
                        {staff.phone}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('status')}>
                        <Tag color={staff.status === 'active' ? 'green' : 'red'}>
                            {t(staff.status)}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('employmentType')}>
                        {staff.employmentType}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('salary')}>
                        {formatCurrency(staff.salary?.amount, staff.salary?.currency)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('paymentFrequency')}>
                        {t(staff.salary?.paymentFrequency || 'monthly')}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('commission')} span={2}>
                        {staff.commission?.enabled ? (
                            <div>
                                {staff.commission.type === 'percentage' ? (
                                    <Tag color="blue">
                                        {staff.commission.percentage}% {t('of')} {
                                            staff.commission.appliesTo === 'course-income' ? t('courseIncome') :
                                                staff.commission.appliesTo === 'all-income' ? t('allIncome') :
                                                    t('specificCourses')
                                        }
                                    </Tag>
                                ) : (
                                    <Tag color="green">
                                        {formatCurrency(staff.commission.fixedAmount, staff.salary?.currency)} {t('fixed')} {t('perCourse')}
                                    </Tag>
                                )}
                                {staff.commission.appliesTo === 'specific-courses' && staff.commission.specificCourses?.length > 0 && (
                                    <div style={{ marginTop: 8 }}>
                                        <small>{t('appliesTo')}: </small>
                                        {staff.commission.specificCourses.map((courseId, index) => {
                                            const course = coursesData?.find(c => c._id === courseId);
                                            return course ? (
                                                <Tag key={courseId} style={{ marginTop: 4 }}>
                                                    {course.name}
                                                </Tag>
                                            ) : null;
                                        })}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Tag color="default">{t('none')}</Tag>
                        )}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('joinDate')}>
                        {staff.joinDate ? dayjs(staff.joinDate).format('YYYY-MM-DD') : '-'}
                    </Descriptions.Item>
                    {staff.dateOfBirth && (
                        <Descriptions.Item label={t('dateOfBirth')}>
                            {dayjs(staff.dateOfBirth).format('YYYY-MM-DD')}
                        </Descriptions.Item>
                    )}
                    {staff.department && (
                        <Descriptions.Item label={t('department')}>
                            {staff.department}
                        </Descriptions.Item>
                    )}
                </Descriptions>

                <h3 style={{ marginTop: 24, marginBottom: 16 }}>{t('teachingCourses')}</h3>
                {teachingCourses.length > 0 ? (
                    <Table
                        columns={courseColumns}
                        dataSource={teachingCourses}
                        rowKey="_id"
                        pagination={false}
                    />
                ) : (
                    <p style={{ color: '#999' }}>{t('notTeachingAnyCourses')}</p>
                )}
            </Card>
        </div>
    );
};

export default StaffDetails;
